/**
 * ScopePicker.tsx
 *
 * The practice/prescriber picker, shown when the server cannot tell which
 * ScriptSure practice and prescriber to open under (a `needsSelection`
 * answer). The patient Prescriptions tab and the Prescribing page both use it.
 *
 * Continue stays disabled until both selects hold ids from the returned lists,
 * and the prefill only ever uses in-list ids: an out-of-list id used to render
 * a blank select with Continue enabled, a guaranteed 422 dead-end.
 */
import { useState } from 'react';
import { Button } from '@/components/ui/Button';
import type { DawSessionSelection } from '@/services/prescriptions.service';

export interface ScopeChoice {
  practiceId: number;
  prescriberId: number;
}

interface ScopeDraft {
  practiceId: number | null;
  prescriberId: number | null;
}

/**
 * What the selects hold on arrival. A choice this user already submitted wins
 * while both of its ids are still in the lists. Otherwise the server's current
 * ids, but only when they exist in the returned option lists, and failing that
 * the only option wherever there is just one.
 */
export function prefillScope(
  selection: DawSessionSelection,
  submitted?: ScopeChoice | null,
): ScopeDraft {
  if (submitted) {
    const practice = selection.practices.find((p) => p.practiceId === submitted.practiceId);
    if (practice?.prescribers.some((pr) => pr.prescriberId === submitted.prescriberId)) {
      return { practiceId: submitted.practiceId, prescriberId: submitted.prescriberId };
    }
  }
  const singlePractice = selection.practices.length === 1 ? selection.practices[0] : undefined;
  const practice =
    selection.practices.find((p) => p.practiceId === selection.currentPracticeId) ??
    singlePractice;
  const singlePrescriber =
    practice && practice.prescribers.length === 1 ? practice.prescribers[0] : undefined;
  const prescriber =
    practice?.prescribers.find((pr) => pr.prescriberId === selection.currentPrescriberId) ??
    singlePrescriber;
  return {
    practiceId: practice?.practiceId ?? null,
    prescriberId: prescriber?.prescriberId ?? null,
  };
}

interface ScopePickerProps {
  selection: DawSessionSelection;
  /**
   * The choice last submitted from this picker, for when it comes back after
   * that submit failed: the user sees what they picked, not the server's
   * default again.
   */
  initialChoice?: ScopeChoice | null;
  onContinue: (choice: ScopeChoice) => void;
}

export function ScopePicker({ selection, initialChoice, onContinue }: ScopePickerProps) {
  const [draft, setDraft] = useState<ScopeDraft>(() => prefillScope(selection, initialChoice));

  // A new answer brings new option lists, so the draft is filled again from
  // it, during this render rather than one frame later from an effect. Keyed
  // on the answer alone: refilling on anything else would throw away what the
  // user is part-way through picking.
  const [filledFrom, setFilledFrom] = useState(selection);
  if (filledFrom !== selection) {
    setFilledFrom(selection);
    setDraft(prefillScope(selection, initialChoice));
  }

  const selectedPractice = selection.practices.find((p) => p.practiceId === draft.practiceId);
  const prescriberInList =
    selectedPractice?.prescribers.some((pr) => pr.prescriberId === draft.prescriberId) ?? false;
  const canContinue = selectedPractice != null && prescriberInList;

  function handleContinue() {
    if (!canContinue || draft.practiceId == null || draft.prescriberId == null) return;
    onContinue({ practiceId: draft.practiceId, prescriberId: draft.prescriberId });
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 py-6">
      {selection.practices.length > 1 && (
        <label className="flex items-center gap-2 text-sm text-slate-600">
          Practice:
          <select
            value={draft.practiceId ?? ''}
            onChange={(e) => {
              const nextId = e.target.value === '' ? null : Number(e.target.value);
              const next = selection.practices.find((p) => p.practiceId === nextId);
              setDraft({
                practiceId: nextId,
                prescriberId:
                  next && next.prescribers.length === 1 ? next.prescribers[0].prescriberId : null,
              });
            }}
            className="max-w-[220px] rounded-md border border-slate-300 px-2 py-1 text-sm"
          >
            <option value="">Select a practice</option>
            {selection.practices.map((practice) => (
              <option key={practice.practiceId} value={practice.practiceId}>
                {practice.name}
              </option>
            ))}
          </select>
        </label>
      )}
      <label className="flex items-center gap-2 text-sm text-slate-600">
        Prescriber:
        <select
          value={draft.prescriberId ?? ''}
          onChange={(e) => {
            const nextId = e.target.value === '' ? null : Number(e.target.value);
            setDraft((current) => ({ ...current, prescriberId: nextId }));
          }}
          disabled={!selectedPractice}
          className="max-w-[220px] rounded-md border border-slate-300 px-2 py-1 text-sm"
        >
          <option value="">Select a prescriber</option>
          {(selectedPractice?.prescribers ?? []).map((prescriber) => (
            <option key={prescriber.prescriberId} value={prescriber.prescriberId}>
              {prescriber.name}
            </option>
          ))}
        </select>
      </label>
      <Button size="sm" onClick={handleContinue} disabled={!canContinue}>
        Continue
      </Button>
    </div>
  );
}
